import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from 'primereact/dropdown';
import { getRequest, postRequest } from '../../services/apiService';
import { XCircle } from 'lucide-react'; // Import XCircle icon

const ProjectWiseMapping = ({ onBack }) => {
  const toast = useRef(null);
  const [projectUserMappings, setProjectUserMappings] = useState([]); // Changed name
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentMapping, setCurrentMapping] = useState(null);
  const [mappingToDelete, setMappingToDelete] = useState(null);

  const [dropdownData, setDropdownData] = useState({
    projects: [], // Changed from departments to projects
    users: []
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    project: "", // Changed from department to project
    user: "",
  });

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });
  const [selectedProjectFilter, setSelectedProjectFilter] = useState(null); // Changed filter
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Fetch initial data
  useEffect(() => {
    fetchMappings();
    fetchDropdownData();
  }, [currentPage, selectedProjectFilter, search, rowsPerPage]); // Updated dependencies

  const fetchMappings = async () => {
    setLoading(true);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        let endpoint = `/config/user-project-mapping/`; // Updated API endpoint
        const response = await getRequest(endpoint);
        let filteredData = response.data || [];

        if (selectedProjectFilter) { // Updated filter logic
          filteredData = filteredData.filter(mapping => mapping.project.id === selectedProjectFilter);
        }

        if (search) {
          const lowerCaseSearch = search.toLowerCase();
          filteredData = filteredData.filter(mapping =>
            mapping.project.name.toLowerCase().includes(lowerCaseSearch) || // Updated search
            mapping.user.loginname.toLowerCase().includes(lowerCaseSearch)
          );
        }

        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        setProjectUserMappings(filteredData.slice(startIndex, endIndex)); // Updated state
        setTotalRecords(filteredData.length);
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch project user mappings.", // Updated message
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const fetchDropdownData = async () => {
    try {
      const [projectsResponse, usersResponse] = await Promise.all([
        getRequest("/master/projects/"), // Updated API endpoint for projects
        getRequest("/api/auth/users/")
      ]);

      setDropdownData({
        projects: projectsResponse.data.map(project => ({ id: project.id, name: project.name })), // Mapped project data
        users: usersResponse.results.data.map(user => ({ id: user.id, name: user.loginname }))
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch dropdown data.",
        life: 3000,
      });
    }
  };

  const mappingFormFields = [
    {
      name: "project", // Changed name
      label: "Project", // Changed label
      type: "select",
      required: true,
      options: [
        { label: "Select Project", value: "" }, // Changed label
        ...(dropdownData.projects || []).map(project => ({ // Mapped project options
          label: project.name,
          value: project.id
        }))
      ],
      helpText: "Select the project" // Updated help text
    },
    {
      name: "user",
      label: "User Name",
      type: "select",
      required: true,
      options: [
        { label: "Select User", value: "" },
        ...(dropdownData.users || []).map(user => ({
          label: user.name,
          value: user.id
        }))
      ],
      helpText: "Select the user for this project" // Updated help text
    },
  ];

  const handleSave = async () => {
    if (!formData.project || !formData.user) { // Updated validation
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please fill in all required fields",
        life: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      if (formData.id) {
        // Update existing mapping using POST method with ID in payload
        await postRequest(`/config/user-project-mapping/`, { // Updated API endpoint
          id: formData.id,
          user: formData.user,
          project: formData.project, // Changed from department to project
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Mapping updated successfully!",
          life: 3000,
        });
      } else {
        // Add new mapping using POST method with status 1
        await postRequest("/config/user-project-mapping/", { // Updated API endpoint
          user: formData.user,
          project: formData.project, // Changed from department to project
          status: 1 // Status 1 for new posts
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Mapping added successfully!",
          life: 3000,
        });
        setCurrentPage(1);
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({ id: null, project: "", user: "" }); // Reset formData
      fetchMappings(); // Re-fetch data to update table
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to save mapping: ${error.response?.data?.message || error.message}`,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({ id: null, project: "", user: "" }); // Reset formData
    setShowAddModal(true);
  };

  const openEditModal = (mapping) => {
    setFormData({
      id: mapping.id,
      project: mapping.project?.id || "", // Changed from department to project
      user: mapping.user?.id || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      // For delete, use POST with id and status 3 in payload
      await postRequest(`/config/user-project-mapping/`, { // Updated API endpoint
        id: mappingToDelete.id,
        status_db: 3
      });
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Mapping deleted successfully!",
        life: 3000,
      });
      setShowDeleteModal(false);
      setMappingToDelete(null);
      setDeleteFormData({ confirmation: "" });
      fetchMappings(); // Re-fetch data to update table
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to delete mapping: ${error.response?.data?.message || error.message}`,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const openDeleteModal = (mapping) => {
    setMappingToDelete(mapping);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const columnFields = [
    {
      field: "project.name", // Changed field
      header: "Project Name", // Changed header
      body: (rowData) => rowData.project?.name || '-' // Changed body
    },
    {
      field: "user.loginname",
      header: "User Name",
      body: (rowData) => rowData.user?.loginname || '-'
    },
  ];

  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData),
    },
  ];

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4 relative">
      <Toast ref={toast} />

      <div className="max-w-full mx-auto">
        {/* Updated structure for filter and close button */}
        <div className="mb-4 p-fluid flex justify-between items-center">
          {/* Filter by Project on the left */}
          <Dropdown
            value={selectedProjectFilter}
            options={[
              { label: "All Projects", value: null }, // Changed label
              ...(dropdownData.projects || []).map(project => ({ // Mapped project options
                label: project.name,
                value: project.id
              }))
            ]}
            onChange={(e) => {
              setSelectedProjectFilter(e.value); // Updated state
              setCurrentPage(1);
            }}
            placeholder="Filter by Project" // Updated placeholder
            showClear
            className="w-64 md:w-14rem text-center border-1 border-black"
          />

          {/* Close Button on the right */}
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors text-gray-600 z-50"
              title="Close"
            >
              <XCircle size={20} />
            </button>
          )}
        </div>
        <div className="mt-0">
          <ReusableTable
            title="Project User Mapping" // Updated title
            data={projectUserMappings} // Updated data prop
            setData={setProjectUserMappings} // Updated setData prop
            columnFields={columnFields}
            setAddNewDialogVisible={openAddModal}
            actionMenu={true}
            actionItems={actionItems}
            loading={loading}
            totalRecords={totalRecords}
            rows={rowsPerPage}
            onPageChange={onPageChange}
            first={(currentPage - 1) * rowsPerPage}
            emptyMessage="No project-user mappings found. Add new mappings to get started." // Updated empty message
            searchValue={search}
            onSearchChange={handleTableSearch}
          />
        </div>

        {/* Add/Edit Mapping Dialog */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className={`bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-2xl`}>
              <ReusableForm
                formData={formData}
                setFormData={setFormData}
                fields={mappingFormFields}
                onSubmit={handleSave}
                onHide={() => (showEditModal ? setShowEditModal(false) : setShowAddModal(false))}
                loading={loading}
                headerText={showEditModal ? "Edit Project User Mapping" : "Add Project User Mapping"} // Updated header text
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Project User Mapping" // Updated title
          description={`Are you sure you want to delete the mapping for project "${mappingToDelete?.project?.name}" and user "${mappingToDelete?.user?.loginname}"? This action cannot be undone. Type 'DELETE' to confirm.`} // Updated description
        />
      </div>
    </div>
  );
};

export default ProjectWiseMapping;